import { supabase } from '../lib/supabaseClient';

interface HealthCheckResult {
  isHealthy: boolean;
  issues: string[];
  performance: {
    responseTime: number;
    memoryUsage: number;
  };
}

interface NavigationContext {
  from: string;
  to: string;
  type: 'sidebar' | 'button' | 'form' | 'programmatic';
  timestamp: number;
}

class NavigationHealthService {
  private healthHistory: HealthCheckResult[] = [];
  private performanceThreshold = 50; // ms
  private memoryThreshold = 100; // MB

  async performHealthCheck(): Promise<HealthCheckResult> {
    const startTime = performance.now();
    const issues: string[] = [];

    try {
      // Basic connection test using auth status (always available)
      // Custom auth kullanıldığı için bu kontrol toleranslı yapılmalı
      try {
        const { error: connectionError } = await supabase.auth.getSession();
        
        if (connectionError && connectionError.message !== 'No session found') {
          // Sadece ciddi hatalar için issue ekle
          if (connectionError.message?.includes('network') || connectionError.message?.includes('timeout')) {
            issues.push('Veritabanı bağlantı sorunu');
          }
        }
      } catch (authError) {
        // Auth hatası görmezden gel (custom auth kullanılıyor)
      }

      // Memory usage check
      const memoryUsage = this.getMemoryUsage();
      if (memoryUsage > this.memoryThreshold) {
        issues.push('Yüksek bellek kullanımı');
      }

      // Response time check
      const responseTime = performance.now() - startTime;
      if (responseTime > this.performanceThreshold) {
        issues.push('Yavaş sistem yanıtı');
      }

      const result: HealthCheckResult = {
        isHealthy: issues.length === 0,
        issues,
        performance: {
          responseTime,
          memoryUsage
        }
      };

      this.healthHistory.push(result);
      this.maintainHistorySize();

      return result;
    } catch (error) {
      return {
        isHealthy: false,
        issues: ['Sistem sağlık kontrolü başarısız'],
        performance: {
          responseTime: performance.now() - startTime,
          memoryUsage: this.getMemoryUsage()
        }
      };
    }
  }

  async preNavigationCheck(context: NavigationContext): Promise<boolean> {
    const healthCheck = await this.performHealthCheck();
    
    if (!healthCheck.isHealthy) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.warn('Navigation health check failed:', {
          context,
          issues: healthCheck.issues
        });
      }
      return false;
    }

    return true;
  }

  getHealthTrend(): 'improving' | 'stable' | 'degrading' {
    if (this.healthHistory.length < 3) return 'stable';

    const recent = this.healthHistory.slice(-3);
    const healthyCount = recent.filter(h => h.isHealthy).length;

    if (healthyCount === 3) return 'stable';
    if (healthyCount >= 2) return 'improving';
    return 'degrading';
  }

  private getMemoryUsage(): number {
    if ('memory' in performance) {
      return (performance as any).memory.usedJSHeapSize / 1024 / 1024;
    }
    return 0;
  }

  private maintainHistorySize(): void {
    if (this.healthHistory.length > 10) {
      this.healthHistory = this.healthHistory.slice(-10);
    }
  }

  clearHistory(): void {
    this.healthHistory = [];
  }
}

export const navigationHealthService = new NavigationHealthService();
export type { HealthCheckResult, NavigationContext };
